import React, { useEffect, useState } from 'react';
import {
  FiCalendar,
  FiCheckCircle,
  FiClock,
  FiPlus,
  FiRefreshCw,
} from 'react-icons/fi';
import DashboardShell from '../../components/DashboardShell/DashboardShell';
import PageHeader from '../../components/PageHeader/PageHeader';
import WalletBadge from '../../components/WalletBadge/WalletBadge';
import DepositModal from '../../components/DepositModal/DepositModal';
import KycBanner from '../../components/KycBanner/KycBanner';
import styles from './PortalInvestments.module.css';
import axiosClient from '../../api/axiosClient';

type Amount = string | number;

interface Investment {
  id: string;
  portal: 'A' | 'B' | 'C';
  invest_amount: Amount;
  term_months: number;
  monthly_capital_pct: Amount;
  monthly_profit_pct: Amount;
  maturity_capital_pct: Amount;
  bct_token_pct: Amount;
  bct_tokens: Amount;
  status: string;
  start_date: string;
  paid_months?: number;
  paid_amount?: Amount;
  credited_months?: number;
  credited_amount?: Amount;
  capital_credited?: Amount;
  profit_credited?: Amount;
  legacy_capital_paid?: Amount;
  legacy_profit_paid?: Amount;
  settled_months?: number;
  total_roi_settled?: Amount;
  remaining_capital?: Amount;
  next_due_date?: string | null;
  next_due_amount?: Amount | null;
  bct_tokens_claimed?: Amount;
  bct_tokens_remaining?: Amount;
  roi_locked_amount?: Amount;
  roi_next_unlock_at?: string | null;
  principal_held?: Amount;
}

interface WalletSummary {
  withdrawnInr: number;
  bonusLockedInr: number;
  nextUnlockAt: string | null;
  principalHeldInr: number;
}

interface Payout {
  id: string;
  month_number: number;
  due_date: string;
  capital_amount: Amount;
  profit_amount: Amount;
  total_amount: Amount;
  is_maturity: boolean;
  status: string;
  roi_status?: 'pending' | 'credited' | 'paid';
  roi_credit_id?: string | null;
  credited_at?: string | null;
  unlocks_at?: string | null;
  paid_at?: string | null;
  is_due?: boolean;
}

const STATUS_COLORS: Record<string, string> = {
  active: '#1d60f6',
  completed: '#5cb87d',
  cancelled: '#e2534a',
};

const money = (value?: Amount | null) => {
  if (value === undefined || value === null || value === '') return '—';

  const amount = Number(value);
  if (!Number.isFinite(amount)) return '—';

  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
};

const quantity = (value?: Amount | null) => {
  if (value === undefined || value === null || value === '') return '—';

  const amount = Number(value);
  if (!Number.isFinite(amount)) return '—';

  return amount.toLocaleString('en-IN', {
    maximumFractionDigits: 6,
  });
};

const dateLabel = (value?: string | null) => {
  if (!value) return '—';

  const parts = value.slice(0, 10).split('-');

  return parts.length === 3
    ? `${parts[2]}/${parts[1]}/${parts[0]}`
    : value.slice(0, 10);
};

const istDateLabel = (value?: string | null) => {
  if (!value) return '—';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';

  return date.toLocaleDateString('en-GB', {
    timeZone: 'Asia/Kolkata',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

const timestampLabel = (value?: string | null) => {
  if (!value) return '—';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';

  return date.toLocaleString('en-IN', {
    timeZone: 'Asia/Kolkata',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const errorMessage = (error: unknown, fallback: string) => {
  const candidate = error as {
    response?: {
      data?: {
        message?: string;
        error?: string;
      };
    };
  };

  return candidate.response?.data?.message
    || candidate.response?.data?.error
    || fallback;
};

const getRoiStatus = (payout: Payout) => {
  if (payout.roi_credit_id || payout.roi_status === 'credited') {
    return 'credited';
  }

  if (payout.roi_status === 'paid' || payout.status === 'paid') {
    return 'paid';
  }

  return 'pending';
};

const filterStyle: React.CSSProperties = {
  padding: '9px 12px',
  borderRadius: 8,
  border: '1px solid var(--line)',
  background: 'var(--card-bg)',
  color: 'var(--cream)',
  maxWidth: '100%',
};

const PortalInvestments: React.FC = () => {
  const [investments, setInvestments] = useState<Investment[]>([]);
  const [walletSummary, setWalletSummary] = useState<WalletSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [refresh, setRefresh] = useState(0);

  const [expanded, setExpanded] = useState<string | null>(null);
  const [payouts, setPayouts] = useState<Payout[]>([]);
  const [payoutsLoading, setPayoutsLoading] = useState(false);
  const [payoutsError, setPayoutsError] = useState('');

  const [monthFilter, setMonthFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showInvest, setShowInvest] = useState(false);

  const reload = () => setRefresh((value) => value + 1);

  useEffect(() => {
    let active = true;

    setLoading(true);
    setLoadError('');

    axiosClient.get('/portal/mine')
      .then(({ data }) => {
        if (!Array.isArray(data.investments)) {
          throw new Error('Invalid investment response');
        }

        if (active) {
          setInvestments(data.investments);
          setWalletSummary(data.summary || null);
          setExpanded((current) =>
            current && data.investments.some(
              (investment: Investment) => investment.id === current,
            )
              ? current
              : null,
          );
        }
      })
      .catch((error: unknown) => {
        if (active) {
          setLoadError(
            errorMessage(error, 'Could not load your investments.'),
          );
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [refresh]);

  useEffect(() => {
    if (!expanded) {
      setPayoutsLoading(false);
      return;
    }

    let active = true;

    setPayouts([]);
    setPayoutsLoading(true);
    setPayoutsError('');

    axiosClient.get(`/portal/mine/${expanded}/payouts`)
      .then(({ data }) => {
        if (!Array.isArray(data.payouts)) {
          throw new Error('Invalid ROI schedule response');
        }

        if (active) setPayouts(data.payouts);
      })
      .catch((error: unknown) => {
        if (active) {
          setPayoutsError(
            errorMessage(error, 'Could not load your ROI schedule.'),
          );
        }
      })
      .finally(() => {
        if (active) setPayoutsLoading(false);
      });

    return () => {
      active = false;
    };
  }, [expanded, refresh]);

  const ownedPortals = Array.from(
    new Set(investments.map((investment) => investment.portal)),
  );

  const canInvestMore = ownedPortals.length < 3;

  const toggleExpand = (investmentId: string) => {
    setPayouts([]);
    setPayoutsError('');
    setPayoutsLoading(expanded !== investmentId);
    setMonthFilter('');
    setStatusFilter('');
    setExpanded((current) =>
      current === investmentId ? null : investmentId,
    );
  };

  const filteredPayouts = payouts
    .filter((payout) =>
      (!monthFilter || payout.due_date.slice(0, 7) === monthFilter)
      && (!statusFilter || getRoiStatus(payout) === statusFilter),
    )
    .sort((a, b) => a.month_number - b.month_number);

  return (
    <DashboardShell>
      <PageHeader
        title="Portal Returns"
        subtitle="Your investment, capital back, profit sharing and monthly ROI credits."
        action={<WalletBadge />}
      />

      <div className={styles.page}>
        <KycBanner />

        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            marginBottom: 16,
          }}
        >
          <button
            type="button"
            className={styles.expandBtn}
            disabled={loading || payoutsLoading}
            onClick={reload}
          >
            <FiRefreshCw />
            {loading ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>

        {loading ? (
          <div className={styles.muted}>Loading your investments…</div>
        ) : loadError ? (
          <div className={styles.emptyState} role="alert">
            <p>{loadError}</p>
            <button
              type="button"
              className={styles.expandBtn}
              style={{ margin: '16px auto 0' }}
              onClick={reload}
            >
              <FiRefreshCw /> Retry
            </button>
          </div>
        ) : (
          <>
            <div className={styles.investCta}>
              <div>
                <b>
                  {canInvestMore
                    ? 'Ready to invest in a new portal?'
                    : 'Your Portal investments'}
                </b>
                <span>
                  {canInvestMore
                    ? `Submit an investment request for Portal ${
                        (['A', 'B', 'C'] as const).filter((key) => !(ownedPortals as string[]).includes(key)).join(' or ')
                      }.`
                    : 'Review each investment and its monthly ROI below.'}
                </span>
              </div>

              {canInvestMore && (
                <button
                  type="button"
                  className={styles.investBtn}
                  onClick={() => setShowInvest(true)}
                >
                  <FiPlus /> Invest Now
                </button>
              )}
            </div>

            {walletSummary && investments.length > 0 && (
              <div className={styles.statsRow} style={{ marginBottom: 16 }}>
                <div>
                  <span>Locked Portal Principal</span>
                  <b>{money(walletSummary.principalHeldInr)}</b>
                </div>
                <div>
                  <span>ROI / Bonus in 30-day wait</span>
                  <b>{money(walletSummary.bonusLockedInr)}</b>
                </div>
                <div>
                  <span>Next Unlock</span>
                  <b>{istDateLabel(walletSummary.nextUnlockAt)}</b>
                </div>
                <div>
                  <span>Withdrawn So Far</span>
                  <b>{money(walletSummary.withdrawnInr)}</b>
                </div>
              </div>
            )}

            {investments.length === 0 && (
              <div className={styles.emptyState}>
                No portal investments are on record yet. Your investment
                and ROI schedule will appear here after confirmation.
              </div>
            )}

            {investments.map((investment) => {
              const settled = investment.settled_months;
              const progress = settled !== undefined
                && investment.term_months > 0
                ? Math.min(
                    100,
                    Math.max(
                      0,
                      (Number(settled) / investment.term_months) * 100,
                    ),
                  )
                : 0;

              const hasEarlierPayments =
                Number(investment.paid_months || 0) > 0;

              return (
                <div key={investment.id} className={styles.card}>
                  <div className={styles.cardHeader}>
                    <div>
                      <div className={styles.portalBadge}>
                        Portal {investment.portal}
                      </div>

                      <h3>{money(investment.invest_amount)} invested</h3>

                      <div className={styles.meta}>
                        {Number(investment.monthly_capital_pct) > 0 && (
                          <>{quantity(investment.monthly_capital_pct)}% capital{' + '}</>
                        )}
                        {quantity(investment.monthly_profit_pct)}% profit
                        {' per month · '}
                        {investment.term_months} months
                      </div>

                      <div className={styles.meta} style={{ marginTop: 6 }}>
                        Start date: {dateLabel(investment.start_date)}
                        {Number(investment.maturity_capital_pct) > 0 && (
                          <>
                            {' · '}
                            {quantity(investment.maturity_capital_pct)}%
                            capital return at maturity
                          </>
                        )}
                      </div>
                    </div>

                    <span
                      className={styles.statusPill}
                      style={{
                        color: STATUS_COLORS[investment.status],
                        borderColor: STATUS_COLORS[investment.status],
                      }}
                    >
                      {investment.status}
                    </span>
                  </div>

                  <div className={styles.progressRow}>
                    <div className={styles.progressBarWrap}>
                      <div
                        className={styles.progressBar}
                        style={{ width: `${progress}%` }}
                      />
                    </div>

                    <span>
                      {settled ?? '—'} of {investment.term_months} months settled
                    </span>
                  </div>

                  <div className={styles.statsRow}>
                    <div>
                      <span>Capital Back Credited</span>
                      <b>{money(investment.capital_credited)}</b>
                    </div>
                    <div>
                      <span>Profit Sharing Credited</span>
                      <b>{money(investment.profit_credited)}</b>
                    </div>
                    <div>
                      <span>Total ROI Bonus Credited</span>
                      <b>{money(investment.credited_amount)}</b>
                    </div>
                    <div>
                      <span>Remaining Capital</span>
                      <b>{money(investment.remaining_capital)}</b>
                    </div>
                    <div>
                      <span>Next Scheduled ROI</span>
                      <b>{money(investment.next_due_amount)}</b>
                    </div>
                    <div>
                      <span>Next Due Date</span>
                      <b>{dateLabel(investment.next_due_date)}</b>
                    </div>
                    <div>
                      <span>Total ROI Settled</span>
                      <b>{money(investment.total_roi_settled)}</b>
                    </div>
                    <div>
                      <span>BCT100 Allocation</span>
                      <b style={{ color: '#a855f7' }}>
                        {quantity(investment.bct_tokens)} BCT100
                      </b>
                    </div>
                    <div>
                      <span>BCT100 Claimed</span>
                      <b style={{ color: '#a855f7' }}>
                        {quantity(investment.bct_tokens_claimed)} BCT100
                      </b>
                    </div>
                    <div>
                      <span>BCT100 Remaining</span>
                      <b style={{ color: '#a855f7' }}>
                        {quantity(investment.bct_tokens_remaining)} BCT100
                      </b>
                    </div>
                    <div>
                      <span>Principal Locked in Wallet</span>
                      <b>{money(investment.principal_held)}</b>
                    </div>
                    <div>
                      <span>ROI in 30-day wait</span>
                      <b>{money(investment.roi_locked_amount)}</b>
                    </div>
                    <div>
                      <span>ROI Unlocks On</span>
                      <b>{istDateLabel(investment.roi_next_unlock_at)}</b>
                    </div>
                  </div>

                  {hasEarlierPayments && (
                    <div className={styles.statsRow}>
                      <div>
                        <span>Earlier Capital Paid</span>
                        <b>{money(investment.legacy_capital_paid)}</b>
                      </div>
                      <div>
                        <span>Earlier Profit Paid</span>
                        <b>{money(investment.legacy_profit_paid)}</b>
                      </div>
                      <div>
                        <span>Earlier Total Paid</span>
                        <b>{money(investment.paid_amount)}</b>
                      </div>
                      <div>
                        <span>Earlier Months Paid</span>
                        <b>{investment.paid_months}</b>
                      </div>
                    </div>
                  )}

                  <button
                    type="button"
                    className={styles.expandBtn}
                    aria-expanded={expanded === investment.id}
                    onClick={() => toggleExpand(investment.id)}
                  >
                    <FiCalendar />
                    {expanded === investment.id ? 'Hide' : 'View'} Monthly ROI Report
                  </button>

                  {expanded === investment.id && (
                    <>
                      <div
                        style={{
                          display: 'flex',
                          gap: 12,
                          flexWrap: 'wrap',
                          alignItems: 'flex-end',
                          marginTop: 16,
                        }}
                      >
                        <label
                          className={styles.meta}
                          style={{ display: 'grid', gap: 6 }}
                        >
                          Due month
                          <input
                            type="month"
                            style={filterStyle}
                            value={monthFilter}
                            onChange={(event) =>
                              setMonthFilter(event.target.value)
                            }
                          />
                        </label>

                        <label
                          className={styles.meta}
                          style={{ display: 'grid', gap: 6 }}
                        >
                          ROI status
                          <select
                            style={filterStyle}
                            value={statusFilter}
                            onChange={(event) =>
                              setStatusFilter(event.target.value)
                            }
                          >
                            <option value="">All statuses</option>
                            <option value="pending">Pending</option>
                            <option value="credited">Wallet credited</option>
                            <option value="paid">Earlier paid</option>
                          </select>
                        </label>

                        <button
                          type="button"
                          className={styles.expandBtn}
                          onClick={() => {
                            setMonthFilter('');
                            setStatusFilter('');
                          }}
                        >
                          Clear Filters
                        </button>
                      </div>

                      <div className={styles.scheduleWrap}>
                        {payoutsLoading ? (
                          <div className={styles.muted}>
                            Loading ROI schedule…
                          </div>
                        ) : payoutsError ? (
                          <div className={styles.muted} role="alert">
                            <p>{payoutsError}</p>
                            <button
                              type="button"
                              className={styles.expandBtn}
                              style={{ margin: '16px auto 0' }}
                              onClick={reload}
                            >
                              Retry
                            </button>
                          </div>
                        ) : filteredPayouts.length === 0 ? (
                          <div className={styles.muted}>
                            No matching ROI entries.
                          </div>
                        ) : (
                          <table className={styles.table}>
                            <thead>
                              <tr>
                                <th>Month</th>
                                <th>Due Date</th>
                                <th>Capital Back Amount</th>
                                <th>Profit Sharing Amount</th>
                                <th>Total ROI Bonus</th>
                                <th>Status</th>
                                <th>Recorded At (IST)</th>
                                <th>Withdrawable From</th>
                              </tr>
                            </thead>

                            <tbody>
                              {filteredPayouts.map((payout) => {
                                const status = getRoiStatus(payout);

                                return (
                                  <tr key={payout.id}>
                                    <td>
                                      {payout.month_number}
                                      {payout.is_maturity && (
                                        <span className={styles.maturityTag}>
                                          Maturity
                                        </span>
                                      )}
                                    </td>

                                    <td>{dateLabel(payout.due_date)}</td>
                                    <td>{money(payout.capital_amount)}</td>
                                    <td>{money(payout.profit_amount)}</td>

                                    <td className={styles.totalCell}>
                                      {money(payout.total_amount)}
                                    </td>

                                    <td>
                                      {status === 'credited' ? (
                                        <span className={styles.paidTag}>
                                          <FiCheckCircle /> Wallet credited
                                        </span>
                                      ) : status === 'paid' ? (
                                        <span className={styles.paidTag}>
                                          <FiCheckCircle /> Earlier paid
                                        </span>
                                      ) : (
                                        <span className={styles.pendingTag}>
                                          <FiClock />
                                          {investment.status === 'cancelled'
                                            ? 'Cancelled investment'
                                            : payout.is_due
                                              ? 'Due — awaiting credit'
                                              : 'Pending'}
                                        </span>
                                      )}
                                    </td>

                                    <td>
                                      {timestampLabel(
                                        status === 'credited'
                                          ? payout.credited_at
                                          : status === 'paid'
                                            ? payout.paid_at
                                            : null,
                                      )}
                                    </td>

                                    <td>
                                      {status === 'credited'
                                        ? (payout.unlocks_at
                                          && new Date(payout.unlocks_at).getTime() > Date.now()
                                          ? `Locked until ${istDateLabel(payout.unlocks_at)}`
                                          : 'Withdrawable')
                                        : '—'}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        )}
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </>
        )}
      </div>

      {showInvest && (
        <DepositModal
          onClose={() => setShowInvest(false)}
          onSuccess={() => {
            setShowInvest(false);
            reload();
          }}
        />
      )}
    </DashboardShell>
  );
};

export default PortalInvestments;